(() => {
  'use strict';
  const media = window.matchMedia('(prefers-reduced-motion: reduce)');
  const smallScreen = window.matchMedia('(max-width: 749px)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const easing = 'cubic-bezier(0.22, 0.68, 0.24, 1)';
  const seen = new WeakSet();
  const pending = new Map();
  const playing = new Map();
  const excluded = 'form, dialog, [role="dialog"], nav, details, [hidden], [aria-hidden="true"], [data-bhs-motion="off"], .slider, .slideshow, .bhs-quote-track, .bhs-disability-track';
  const cards = '.bhs-photo-card, .bhs-service-links > a, .bhs-benefit, .bhs-journey-card, .bhs-feature-card, .bhs-v2-card, .bhs-v2-icon-card, .bhs-v2-process-card, .bhs-state-card, .bhs-result-card, .bhs-popular-grid > a, .bhs-video-card, .blog-articles__article, .multicolumn-list__item, .collection-list__item';
  const buttons = '.bhs-button, .bhs-v2-button, .button, .btn, .sp-button, .faq-btn, .tpt-button__primary, .bhs-feature-action, .bhs-journey-link';
  const pictures = '.bhs-v2-split-media, .bhs-benefit-photo, .image-with-text__media';
  const introductions = '.bhs-section-intro, .bhs-v2-section-head, .bhs-finale > div, .bhs-care-banner > div';
  const prose = 'h2, h3, .rte > p, .rte > ul, .rte > ol, .rte > blockquote, .rte > figure, .bhs-v2-prose > p, .bhs-v2-prose > ul, .bhs-v2-prose > ol, .bhs-v2-prose > blockquote, .bhs-benefit-copy > p';
  let observer;
  const enabled = () => !media.matches && !window.Shopify?.designMode;
  const stop = element => {
    const animation = playing.get(element);
    if (animation) animation.cancel();
    playing.delete(element);
  };
  const animate = (element, kind, delay = 0, entrance = false) => {
    if (!enabled() || typeof element.animate !== 'function') return;
    stop(element);
    const distance = kind === 'disclosure' ? 8 : smallScreen.matches ? 14 : 24;
    let from = `0 ${distance}px`;
    if (kind === 'image' && !smallScreen.matches) {
      const rect = element.getBoundingClientRect();
      from = `${rect.left + rect.width / 2 < innerWidth / 2 ? -32 : 32}px 0`;
    }
    const frames = entrance
      ? [{ translate: '0 12px' }, { translate: '0 0' }]
      : [{ opacity: 0, translate: from }, { opacity: 1, translate: '0 0' }];
    try {
      const animation = element.animate(frames, {
        duration: kind === 'disclosure' ? 240 : smallScreen.matches ? 460 : kind === 'image' ? 760 : 620,
        delay, easing, fill: 'backwards'
      });
      playing.set(element, animation);
      animation.finished.then(() => {
        if (playing.get(element) === animation) playing.delete(element);
        animation.cancel();
      }, () => {});
    } catch {
      stop(element);
    }
  };
  const reveal = element => {
    observer?.unobserve(element);
    pending.delete(element);
    stop(element);
  };
  const observe = () => {
    if (!('IntersectionObserver' in window)) return null;
    return new IntersectionObserver(entries => {
      const visible = entries.filter(entry => entry.isIntersecting).sort((a, b) => {
        const row = a.boundingClientRect.top - b.boundingClientRect.top;
        return Math.abs(row) > 30 ? row : a.boundingClientRect.left - b.boundingClientRect.left;
      });
      const rows = new Map();
      visible.forEach(entry => {
        const element = entry.target;
        const kind = pending.get(element);
        observer.unobserve(element);
        pending.delete(element);
        if (!kind || element.contains(document.activeElement) || element.closest(excluded)) return;
        const parent = element.parentElement;
        const count = rows.get(parent) || 0;
        rows.set(parent, count + 1);
        const rect = entry.boundingClientRect;
        if (rect.bottom <= 0 || rect.top >= innerHeight || rect.width === 0) return;
        animate(element, kind, Math.min(count, 3) * (smallScreen.matches ? 45 : 75));
      });
    }, { threshold: 0, rootMargin: '0px 0px -32px 0px' });
  };
  const init = (scope = document, firstLoad = false) => {
    const main = document.getElementById('MainContent') || document.querySelector('main') || document.body;
    const root = scope === document ? main : scope;
    if (!main.contains(root) && root !== main) return;
    root.querySelectorAll(cards).forEach(element => {
      if (!element.closest(excluded)) element.dataset.bhsMotionCard = '';
    });
    root.querySelectorAll(buttons).forEach(element => {
      if (!element.closest('dialog, [role="dialog"]')) element.dataset.bhsMotionButton = '';
    });
    if (!enabled() || !observer) return;
    const candidates = new Map();
    [[cards, 'card'], [pictures, 'image'], [introductions, 'intro'], [prose, 'text']].forEach(([selector, kind]) => {
      root.querySelectorAll(selector).forEach(element => {
        if (!candidates.has(element)) candidates.set(element, kind);
      });
    });
    for (const [element, kind] of candidates) {
      if (seen.has(element) || element.closest(excluded) || element.closest('.bhs-hero, .bhs-v2-hero')) continue;
      let parent = element.parentElement;
      let nested = false;
      while (parent && parent !== main) {
        if (candidates.has(parent)) { nested = true; break; }
        parent = parent.parentElement;
      }
      if (nested) continue;
      const rect = element.getBoundingClientRect();
      if (!rect.width || !rect.height || rect.height > innerHeight * 1.35) continue;
      seen.add(element);
      element.dataset.bhsMotion = kind;
      if (rect.top < innerHeight && rect.bottom > 0 || rect.bottom <= 0) continue;
      pending.set(element, kind);
      observer.observe(element);
    }
    if (firstLoad && scrollY < 8 && !location.hash) {
      main.querySelectorAll('.bhs-hero-copy > .bhs-eyebrow, .bhs-hero-copy > .bhs-actions, .bhs-v2-hero-copy > .bhs-v2-eyebrow, .bhs-v2-hero-copy > .bhs-v2-actions').forEach(element => {
        if (element.getBoundingClientRect().top < innerHeight) animate(element, 'text', 0, true);
      });
    }
  };
  const finishAll = () => {
    observer?.disconnect();
    pending.clear();
    for (const element of playing.keys()) stop(element);
  };
  const start = () => {
    observer = observe();
    init(document, true);
    document.addEventListener('shopify:section:load', event => init(event.target));
    document.addEventListener('shopify:section:unload', event => {
      for (const element of pending.keys()) if (event.target.contains(element)) reveal(element);
      for (const element of playing.keys()) if (event.target.contains(element)) stop(element);
    });
    document.addEventListener('focusin', event => {
      let element = event.target;
      while (element && element !== document.body) { reveal(element); element = element.parentElement; }
    });
    document.addEventListener('keydown', event => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'f') finishAll();
    });
    document.addEventListener('toggle', event => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.open || !enabled()) return;
      if (!details.closest('#MainContent, .bhs-site-header')) return;
      const content = Array.from(details.children).find(element => element.tagName !== 'SUMMARY');
      if (content && content.getBoundingClientRect().height < innerHeight) animate(content, 'disclosure');
    }, true);
    document.addEventListener('pointerover', event => {
      if (finePointer.matches) {
        const target = event.target.closest?.('[data-bhs-motion-card], [data-bhs-motion-button]');
        if (target) reveal(target);
      }
    }, { passive: true });
    media.addEventListener('change', finishAll);
    window.addEventListener('pagehide', finishAll);
    window.addEventListener('beforeprint', finishAll);
    window.addEventListener('hashchange', finishAll);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
